<template>
  <section class="selection-gallery">
    <div class="gallery-toolbar">
      <div class="toolbar-left">
        <span class="selection-count">{{ selectedImages.length }} selected</span>
        <button
          class="select-all-btn"
          type="button"
          @click="toggleSelectAll"
          :disabled="images.length === 0"
        >
          {{ allSelected ? 'Clear all' : 'Select all' }}
        </button>
      </div>
      <button
        class="view-cart-btn"
        type="button"
        @click="$emit('view-cart')"
        :disabled="selectedImages.length === 0"
      >
        View cart
      </button>
    </div>

    <div ref="grid" class="gallery-grid" :style="gridStyle">
      <div
        v-for="(image, index) in images"
        :key="image.src || index"
        class="gallery-item"
        :style="itemStyle(index)"
        role="button"
        tabindex="0"
        @click="openLightbox(index)"
        @keydown.enter.prevent="openLightbox(index)"
        @keydown.space.prevent="openLightbox(index)"
      >
        <div v-if="image.isNew" class="new-pill">New</div>
        <img
          ref="gridImage"
          :src="image.src"
          :alt="image.alt || 'Snapshot preview'"
          class="gallery-image"
          loading="lazy"
          @load="onImageLoad(index, $event)"
        />
        <button
          class="select-toggle"
          type="button"
          @click.stop="toggleImage(index)"
          :aria-pressed="isSelected(image) ? 'true' : 'false'"
          :aria-label="isSelected(image) ? 'Remove from cart' : 'Add to cart'"
        >
          <span
            class="checkbox"
            :class="{ checked: isSelected(image) }"
          ></span>
          <span class="toggle-label">
            {{ isSelected(image) ? 'Selected' : 'Select' }}
          </span>
        </button>
      </div>
    </div>

    <transition name="lightbox-fade">
      <div
        v-if="lightboxOpen"
        ref="lightbox"
        class="lightbox"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="lightbox-backdrop" @click="closeLightbox"></div>
        <div
          class="lightbox-content"
          @touchstart.passive="onTouchStart"
          @touchend="onTouchEnd"
        >
          <button
            class="lightbox-close"
            type="button"
            @click="closeLightbox"
            aria-label="Close"
          >
            &times;
          </button>

          <button
            class="lightbox-nav lightbox-prev"
            type="button"
            @click="goPrevious"
            :disabled="!hasPrevious"
            aria-label="Previous image"
          >
            &lt;
          </button>

          <img
            class="lightbox-image"
            :src="currentImage.src"
            :alt="currentImage.alt || 'Snapshot preview'"
          />

          <button
            class="lightbox-nav lightbox-next"
            type="button"
            @click="goNext"
            :disabled="!hasNext"
            aria-label="Next image"
          >
            &gt;
          </button>

          <div class="lightbox-footer">
            <div class="lightbox-count">
              {{ lightboxIndex + 1 }} / {{ images.length }}
            </div>
            <button
              v-if="directDownloadEnabled"
              class="lightbox-download"
              type="button"
              :disabled="downloadInProgress"
              @click="$emit('download-image', currentImage)"
            >
              {{ downloadInProgress ? 'Preparing…' : 'Download photo' }}
            </button>
            <button
              class="lightbox-select"
              type="button"
              @click="toggleImage(lightboxIndex)"
            >
              {{ isSelected(currentImage) ? 'Remove from cart' : 'Add to cart' }}
            </button>
          </div>
          <p v-if="downloadError && directDownloadEnabled" class="lightbox-download-error" role="alert">
            {{ downloadError }}
          </p>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
export default {
  name: "SnapshotSelectionGalleryNew",
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    selectedImages: {
      type: Array,
      default: () => [],
    },
    directDownloadEnabled: { type: Boolean, default: false },
    downloadInProgress: { type: Boolean, default: false },
    downloadError: { type: String, default: "" },
  },
  data() {
    return {
      gridRowHeight: 8,
      gridGap: 12,
      gridPadding: 12,
      rowSpans: [],
      lightboxOpen: false,
      lightboxIndex: 0,
      savedScrollY: 0,
      touchStartX: null,
    };
  },
  computed: {
    gridStyle() {
      return {
        "--gallery-row-height": `${this.gridRowHeight}px`,
        "--gallery-gap": `${this.gridGap}px`,
        "--gallery-padding": `${this.gridPadding}px`,
      };
    },
    currentImage() {
      return this.images && this.images.length
        ? this.images[this.lightboxIndex]
        : { src: "", alt: "" };
    },
    hasPrevious() {
      return this.lightboxIndex > 0;
    },
    hasNext() {
      return this.images && this.lightboxIndex < this.images.length - 1;
    },
    allSelected() {
      return this.images.length > 0 && this.selectedImages.length === this.images.length;
    },
  },
  watch: {
    images() {
      this.rowSpans = [];
      if (this.lightboxIndex >= this.images.length) {
        this.lightboxIndex = Math.max(this.images.length - 1, 0);
      }
      this.$nextTick(() => {
        this.recalculateLayout();
      });
    },
  },
  mounted() {
    window.addEventListener("resize", this.recalculateLayout);
    window.addEventListener("keydown", this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.recalculateLayout);
    window.removeEventListener("keydown", this.onKeyDown);
    this.unlockScroll();
  },
  methods: {
    isSelected(image) {
      return this.selectedImages.includes(image);
    },
    toggleImage(index) {
      this.$emit("toggle-image", index);
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.$emit("clear-all");
        return;
      }
      this.$emit("select-all");
    },
    openLightbox(index) {
      this.lightboxIndex = index;
      this.lightboxOpen = true;
      this.lockScroll();
      this.preloadAround(index);
      this.$nextTick(() => {
        if (this.$refs.lightbox) this.$refs.lightbox.focus();
      });
    },
    closeLightbox() {
      this.lightboxOpen = false;
      this.unlockScroll();
    },
    goPrevious() {
      if (!this.hasPrevious) return;
      this.lightboxIndex -= 1;
      this.preloadAround(this.lightboxIndex);
    },
    goNext() {
      if (!this.hasNext) return;
      this.lightboxIndex += 1;
      this.preloadAround(this.lightboxIndex);
    },
    onKeyDown(event) {
      if (!this.lightboxOpen) return;
      if (event.key === "Escape") {
        this.closeLightbox();
        return;
      }
      if (event.key === "ArrowLeft") {
        this.goPrevious();
        return;
      }
      if (event.key === "ArrowRight") {
        this.goNext();
      }
    },
    onTouchStart(event) {
      if (!event.changedTouches || !event.changedTouches.length) return;
      this.touchStartX = event.changedTouches[0].clientX;
    },
    onTouchEnd(event) {
      if (this.touchStartX === null) return;
      if (!event.changedTouches || !event.changedTouches.length) return;
      const touchEndX = event.changedTouches[0].clientX;
      const deltaX = touchEndX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(deltaX) < 40) return;
      if (deltaX > 0) {
        this.goPrevious();
        return;
      }
      this.goNext();
    },
    preloadAround(index) {
      const candidates = [index - 1, index + 1];
      candidates.forEach((i) => {
        if (i < 0 || i >= this.images.length) return;
        const src = this.images[i] && this.images[i].src;
        if (!src) return;
        const img = new Image();
        img.src = src;
      });
    },
    itemStyle(index) {
      const fallbackRows = 12;
      return {
        gridRowEnd: `span ${this.rowSpans[index] || fallbackRows}`,
      };
    },
    onImageLoad(index, event) {
      const img = event && event.target ? event.target : null;
      if (!img) return;
      const item = img.closest(".gallery-item");
      if (!item) return;
      const width = item.clientWidth || img.clientWidth || 1;
      const naturalWidth = img.naturalWidth || width;
      const naturalHeight = img.naturalHeight || width;
      const displayHeight = Math.max((naturalHeight / naturalWidth) * width, 120);
      const rowSpan = Math.ceil(
        (displayHeight + this.gridGap) / (this.gridRowHeight + this.gridGap)
      );
      this.$set(this.rowSpans, index, rowSpan);
      item.style.gridRowEnd = `span ${rowSpan}`;
    },
    recalculateLayout() {
      if (!this.$refs.gridImage) return;
      const images = Array.isArray(this.$refs.gridImage)
        ? this.$refs.gridImage
        : [this.$refs.gridImage];
      images.forEach((img, index) => {
        if (!img) return;
        this.onImageLoad(index, { target: img });
      });
    },
    lockScroll() {
      if (typeof window === "undefined") return;
      if (typeof document === "undefined" || !document.body) return;
      this.savedScrollY = window.scrollY || 0;
      document.body.style.top = `-${this.savedScrollY}px`;
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    },
    unlockScroll() {
      if (typeof window === "undefined") return;
      if (typeof document === "undefined" || !document.body) return;
      if (document.body.style.position !== "fixed") return;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, this.savedScrollY || 0);
    },
  },
};
</script>

<style scoped>
.selection-gallery {
  width: 100%;
  background: #f7f6f2;
  border-radius: 16px;
}

.gallery-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 4px;
  gap: 12px;
  flex-wrap: wrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selection-count {
  font-weight: 600;
  color: #1f2933;
}

.select-all-btn {
  background: transparent;
  border: 1px solid rgba(31, 41, 51, 0.2);
  color: #1f2933;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.9rem;
}

.select-all-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.view-cart-btn {
  background: #08697b;
  color: #ffffff;
  border: none;
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: 600;
}

.view-cart-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  grid-auto-rows: var(--gallery-row-height);
  grid-auto-flow: dense;
  gap: var(--gallery-gap);
  padding: var(--gallery-padding);
}

.gallery-item {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-item:focus,
.gallery-item:hover {
  outline: none;
  transform: translateY(-2px);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.select-toggle {
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff;
  border: none;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
}

.new-pill {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #08697b;
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  box-shadow: 0 6px 16px rgba(8, 105, 123, 0.25);
}

.checkbox {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid #ffffff;
  display: inline-block;
  position: relative;
}

.checkbox.checked {
  background: #facc15;
  border-color: #facc15;
}

.checkbox.checked::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 5px;
  width: 4px;
  height: 8px;
  border: solid #111827;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 11, 0.85);
}

.lightbox-content {
  position: relative;
  z-index: 1;
  width: min(92vw, 980px);
  max-height: 90vh;
  background: #0f1115;
  border-radius: 20px;
  padding: 20px;
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  grid-template-rows: 1fr auto;
  gap: 12px;
  align-items: center;
}

.lightbox-image {
  grid-column: 2;
  grid-row: 1;
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 12px;
  background: #0b0d12;
}

.lightbox-close {
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  color: #ffffff;
  border: none;
  font-size: 28px;
  cursor: pointer;
}

.lightbox-nav {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  font-size: 24px;
  cursor: pointer;
}

.lightbox-nav:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.lightbox-prev {
  grid-column: 1;
  grid-row: 1;
}

.lightbox-next {
  grid-column: 3;
  grid-row: 1;
}

.lightbox-footer {
  grid-column: 1 / -1;
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  color: #e5e7eb;
}

.lightbox-download {
  background: #08697b;
  color: #ffffff;
  border: 0;
  border-radius: 999px;
  padding: 10px 20px;
  font-weight: 700;
  cursor: pointer;
}

.lightbox-download:hover { background: #075666; }
.lightbox-download:disabled { opacity: 0.6; cursor: wait; }
.lightbox-download-error {
  grid-column: 1 / -1;
  grid-row: 3;
  color: #fecaca;
  margin: 0;
}

.lightbox-count {
  font-size: 0.95rem;
}

.lightbox-select {
  background: #facc15;
  color: #111827;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-weight: 700;
  cursor: pointer;
}

.lightbox-select:hover {
  background: #fbbf24;
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter,
.lightbox-fade-leave-to {
  opacity: 0;
}

@media (min-width: 768px) {
  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    padding: 16px 24px 24px;
  }

  .lightbox-content {
    padding: 28px;
  }
}

@media (max-width: 640px) {
  .lightbox-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto auto;
  }

  .lightbox-image {
    grid-column: 1;
    grid-row: 2;
    max-height: 60vh;
  }

  .lightbox-prev,
  .lightbox-next {
    grid-row: 1;
    grid-column: auto;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .lightbox-prev {
    left: 12px;
  }

  .lightbox-next {
    right: 12px;
  }

  .lightbox-footer {
    grid-row: 3;
    flex-direction: column;
    gap: 12px;
  }
  .lightbox-download-error { grid-row: 4; }
  .lightbox-download, .lightbox-select { width: 100%; }
}
</style>
